import React, { useCallback, useMemo } from "react";
import {
  getLoginDialogTitleAndDescription,
  getShouldShow,
} from "scmp-sdk/components/acquisition-popup-container/display/helpers";
import type {
  PopRenderPayload,
  PopupGetter,
  useRenderPopupOptions,
} from "scmp-sdk/components/acquisition-popup-container/display/types";
import { useSdkContext } from "scmp-sdk/components/contexts";
import { isFollowType } from "scmp-sdk/components/follow-button/helpers";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { CampaignPopup } from ".";
import { parseConfigFromQueryAction } from "./actions";
import { SanitizedUrlKeys } from "./consts";

export const useCampaignPopupGetter = (options: useRenderPopupOptions): PopupGetter => {
  const {
    acquisition: { popupState },
    currentRoute,
    handleOpenLoginDialog,
    handleRemoveRouteQuery: handleRemoveRouteQuery_,
    userCountry,
    userTypeIds,
  } = useSdkContext();

  const handleRemoveRouteQuery = useCallback(
    () => handleRemoveRouteQuery_(SanitizedUrlKeys),
    [handleRemoveRouteQuery_],
  );

  const getNextFromAction = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const parsedConfig = parseConfigFromQueryAction(
        new URL(currentRoute, globalThis.location.origin).search,
      );
      if (!parsedConfig) return;

      const matchedCampaign = Object.values(popupState.campaigns).find(
        campaign => campaign.name === parsedConfig.campaign,
      );
      if (!matchedCampaign) return;

      if (!options.isLoggedIn) {
        const { description, title } = getLoginDialogTitleAndDescription({
          campaignName: matchedCampaign.campaign,
          type: matchedCampaign.type,
        });

        handleOpenLoginDialog({
          description,
          destination: globalThis.location.href,
          ga4CustomParameter: {
            internal_campaign: matchedCampaign.campaign,
            trigger_point: "ppto",
          },
          title,
        });
        void handleRemoveRouteQuery();
        return;
      }

      const props = {
        campaign: matchedCampaign,
        confirmedAction: true,
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
      };

      void handleRemoveRouteQuery();

      return {
        element: <CampaignPopup {...props} />,
        localStorageKey: matchedCampaign.localStorageKey,
      };
    },
    [
      currentRoute,
      popupState.campaigns,
      options.isLoggedIn,
      options.modalContainerReference,
      handleRemoveRouteQuery,
      handleOpenLoginDialog,
    ],
  );

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const getNextFromConfig = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const nextConfig = Object.values(popupState.campaigns).find(
        campaign =>
          isFollowType(campaign.type) &&
          getShouldShow({
            config: campaign,
            userInfo: {
              isLoggedIn: options.isLoggedIn,
              userCountry: userCountry ?? "",
              userDevice: isMobile ? "mobile" : "desktop",
              userTypeIds,
            },
          }),
      );

      if (!nextConfig) return;

      const props = {
        campaign: nextConfig,
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
      };
      return {
        delay: Number.parseInt(nextConfig.timeout ?? "0", 10),
        element: <CampaignPopup {...props} />,
        localStorageKey: nextConfig.localStorageKey,
      };
    },
    [
      isMobile,
      options.isLoggedIn,
      options.modalContainerReference,
      popupState.campaigns,
      userCountry,
      userTypeIds,
    ],
  );

  const getters = useMemo(
    () => ({ getNextFromAction, getNextFromConfig }),
    [getNextFromConfig, getNextFromAction],
  );

  return getters;
};
